import { InterceptorManagerImpl } from "./interceptors.js";
class HTTPServiceError extends Error {
    constructor(message, config, response, isTimeout = false, isNetworkError = false){
        super(message);
        this.name = 'HTTPServiceError';
        this.config = config;
        this.response = response;
        this.isTimeout = isTimeout;
        this.isNetworkError = isNetworkError;
    }
}
class HTTPService {
    constructor(config = {}){
        this.interceptors = {
            request: new InterceptorManagerImpl(),
            response: new InterceptorManagerImpl()
        };
        this.config = {
            timeout: 30000,
            retries: 0,
            retryDelay: 1000,
            ...config
        };
        this.setupInterceptors();
    }
    setupInterceptors() {}
    async request(config) {
        let processedConfig = this.mergeConfig(config);
        try {
            processedConfig = await this.processRequestInterceptors(processedConfig);
            const response = await this.executeRequest(processedConfig);
            return await this.processResponseInterceptors(response);
        } catch (error) {
            const httpError = error instanceof HTTPServiceError ? error : new HTTPServiceError(error instanceof Error ? error.message : 'Unknown error', processedConfig, void 0, false, !navigator.onLine);
            throw await this.processResponseErrorInterceptors(httpError);
        }
    }
    async get(url, config) {
        return this.request({
            ...config,
            url,
            method: 'GET'
        });
    }
    async post(url, body, config) {
        return this.request({
            ...config,
            url,
            method: 'POST',
            body
        });
    }
    async put(url, body, config) {
        return this.request({
            ...config,
            url,
            method: 'PUT',
            body
        });
    }
    async patch(url, body, config) {
        return this.request({
            ...config,
            url,
            method: 'PATCH',
            body
        });
    }
    async delete(url, config) {
        return this.request({
            ...config,
            url,
            method: 'DELETE'
        });
    }
    async head(url, config) {
        return this.request({
            ...config,
            url,
            method: 'HEAD'
        });
    }
    async options(url, config) {
        return this.request({
            ...config,
            url,
            method: 'OPTIONS'
        });
    }
    mergeConfig(config) {
        const url = this.config.baseURL ? `${this.config.baseURL}${config.url}`.toString() : config.url;
        return {
            method: 'GET',
            headers: {
                ...this.config.defaultHeaders,
                ...config.headers
            },
            timeout: this.config.timeout,
            retries: this.config.retries,
            retryDelay: this.config.retryDelay,
            ...config,
            url
        };
    }
    async processRequestInterceptors(config) {
        let processedConfig = config;
        const requestInterceptors = this.interceptors.request.getRequestInterceptors();
        for (const interceptor of requestInterceptors)try {
            processedConfig = await interceptor.onRequest(processedConfig);
        } catch (error) {
            if (interceptor.onRequestError) {
                const processedError = await interceptor.onRequestError(error instanceof Error ? error : new Error('Unknown error'));
                throw processedError;
            }
            throw error;
        }
        return processedConfig;
    }
    async processResponseInterceptors(response) {
        let processedResponse = response;
        const responseInterceptors = this.interceptors.response.getResponseInterceptors();
        for (const interceptor of responseInterceptors)processedResponse = await interceptor.onResponse(processedResponse);
        return processedResponse;
    }
    async processResponseErrorInterceptors(error) {
        let processedError = error;
        const responseInterceptors = this.interceptors.response.getResponseInterceptors();
        for (const interceptor of responseInterceptors)if (interceptor.onResponseError) try {
            processedError = await interceptor.onResponseError(processedError);
        } catch (retryError) {
            if (retryError instanceof Error && 'RETRY_REQUEST' === retryError.message) return this.request(error.config);
            throw retryError;
        }
        return processedError;
    }
    async executeRequest(config) {
        const controller = new AbortController();
        const timeoutId = config.timeout ? setTimeout(()=>controller.abort(), config.timeout) : null;
        try {
            const fetchOptions = {
                method: config.method,
                headers: this.prepareHeaders(config.headers, config.body),
                body: this.prepareBody(config.body),
                signal: controller.signal
            };
            const response = await fetch(config.url, fetchOptions);
            if (timeoutId) clearTimeout(timeoutId);
            if (!response.ok) {
                const errorData = await this.parseResponseData(response);
                throw new HTTPServiceError(`HTTP Error: ${response.status} ${response.statusText}`, config, {
                    data: errorData,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                    config
                });
            }
            const data = await this.parseResponseData(response);
            return {
                data,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                config
            };
        } catch (error) {
            if (timeoutId) clearTimeout(timeoutId);
            if (error instanceof Error && 'AbortError' === error.name) throw new HTTPServiceError('Request timeout', config, void 0, true);
            if (error instanceof HTTPServiceError) throw error;
            throw new HTTPServiceError(error instanceof Error ? error.message : 'Network error', config, void 0, false, true);
        }
    }
    prepareHeaders(headers = {}, body) {
        const preparedHeaders = {
            ...headers
        };
        if (void 0 !== body && !preparedHeaders['Content-Type']) {
            if ('object' == typeof body && null !== body) preparedHeaders['Content-Type'] = 'application/json';
            else if ('string' == typeof body) preparedHeaders['Content-Type'] = 'text/plain';
        }
        return preparedHeaders;
    }
    prepareBody(body) {
        if (null == body) return;
        if (body instanceof FormData || body instanceof URLSearchParams) return body;
        if ('object' == typeof body) return JSON.stringify(body);
        return String(body);
    }
    async parseResponseData(response) {
        const contentType = response.headers.get('content-type');
        if (!contentType) return;
        if (contentType.includes('application/json')) {
            const text = await response.text();
            return text ? JSON.parse(text) : void 0;
        }
        if (contentType.includes('text/')) return await response.text();
        if (contentType.includes('application/octet-stream') || contentType.includes('application/')) return await response.arrayBuffer();
        return await response.blob();
    }
}
export { HTTPService };
